import { Box, Button } from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';

import Error from '../atoms/Error';
import Header from '../molecules/Header';
import Locale from '../Locale';
import PropTypes from 'prop-types';
import React from 'react';
import SubmitButton from '../SubmitButton';
import get from 'lodash/get';
import set from 'lodash/set';
import startCase from 'lodash/startCase';
import { translate } from '../../helpers/i18n';
import useBoundCallback from '../../helpers/Hooks/useBoundCallback';
import { v4 as uuid } from 'uuid';

const scrollToRef = ref => ref.current && window.scrollTo(0, ref.current.offsetTop);

/**
 * @typedef {Object} FormikValidationFormProps
 * @property {string} [autoComplete] The autocomplete attribute for the form element.
 * @property {string} [disclaimer] A string, or localized string path, that displays after the form, but before the error and action buttons.
 * @property {string} [error] Error provided to the dialog to display. This is helpful if there is external validation logic that can provide an error.
 * @property {React.RefObject<ReturnType<import('formik')['useFormik']>>} [formikRef] Formik ref to be used to access the FormikBag context outside of the component
 * @property {string} [formId] An id to attach to the Confirm button that will associate it to a given form field. If you do not provide it, one will be created.
 * @property {any} [initialValues] The initialValues of the form provided to the `useFormik` hook.
 * @property {string} [title] The title that appears in the Dialog header
 * @property {string} [subtitle] The subtitle that appears in the Dialog header
 * @property {React.ReactNode} [children] The form fields to render inside the form. This can be a function that returns the form fields, or a React node.
 * @property {boolean} [enableReinitialize] Whether to reinitialize the form when initialValues change.
 * @property {Record<string,any>} [translationOptions] The translation options provided to all localized strings in the Dialog
 * @property {import('formik').FormikConfig['validate']} validate The validate function provided to `useFormik`
 * @property {boolean} [validateOnBlur] The validateOnBlur provided to `useFormik`
 * @property {boolean} [validateOnChange] The validateOnChange provided to `useFormik`
 * @property {boolean} [validateOnMount] The validateOnMount provided to `useFormik`
 * @property {(import('yup').AnyObjectSchema|(values: import('formik').FormikValues) => import('yup').AnyObjectSchema)} [validationSchema] The validationSchema provided to `useFormik`.
 */

/**
 * A Formik-based validation form component.
 */
const FormikValidationForm =
    /** @type {React.ForwardRefExoticComponent<FormikValidationFormProps>} */ (
        React.forwardRef(function FormikValidationForm(props, ref) {
            const errorRef = React.useRef(null);
            const id = React.useRef(props.formId || uuid());
            const SubmitButtonComponent = props.submitButton || SubmitButton;
            const ResetButtonComponent = props.resetButton || Button;

            const mounted = React.useRef(null);
            const [submitError, setSubmitError] = React.useState(props.error || '');

            const validate = useBoundCallback(
                async (validate, validationSchema, values) => {
                    let errors = {};
                    if (validationSchema) {
                        try {
                            validationSchema.validateSync(values, {
                                abortEarly: false,
                            });
                        } catch (validationErrors) {
                            errors = (validationErrors.inner || []).reduce((acc, fieldError) => {
                                const { path, message } = fieldError;
                                const currentErrors = get(acc, path, []);
                                if (message.props) {
                                    // This is a <Locale /> component default error
                                    currentErrors.push(
                                        translate(message.props.path, message.props.options)
                                    );
                                } else if (message) {
                                    currentErrors.push(
                                        translate(`${message}`, {
                                            value: get(values, path),
                                            name: startCase(path.split('.').pop()),
                                        })
                                    );
                                } else {
                                    return acc;
                                }
                                acc = set(acc, path, currentErrors);
                                return acc;
                            }, errors);
                        }
                    }
                    if (validate) {
                        const customErrors = await validate(values);
                        if (customErrors) {
                            errors = { ...errors, ...customErrors };
                        }
                    }

                    return errors;
                },
                [props.validate, props.validationSchema]
            );

            const onReset = useBoundCallback(
                /** @type {(onReset: (values: import('formik').FormikValues) => void, values: import('formik').FormikValues) => void} */
                (onReset, values) => {
                    setSubmitError('');
                    return (onReset && onReset(values)) || Promise.resolve(values);
                },
                [props.onReset]
            );

            const onSubmit = useBoundCallback(
                /** @type {(onSubmit: (values: import('formik').FormikValues) => Promise<any> | void, values: import('formik').FormikValues, formikHelpers: import('formik').FormikHelpers) => Promise<any>} */
                (onSubmit, values) => {
                    setSubmitError('');
                    return ((onSubmit && onSubmit(values)) || Promise.resolve(values)).catch(
                        error => {
                            const { message = error } = error;
                            if (mounted.current) {
                                setSubmitError(message);
                            }
                            throw error;
                        }
                    );
                },
                [props.onSubmit]
            );

            const {
                enableReinitialize,
                initialValues,
                initialTouched,
                validateOnBlur,
                validateOnChange,
                validateOnMount,
                validationSchema,
            } = props;
            const formikBag = useFormik({
                validate,
                initialTouched,
                initialValues,
                onReset,
                onSubmit,
                validateOnMount,
                validateOnBlur,
                validateOnChange,
                validationSchema,
                enableReinitialize,
            });
            props.formikRef && (props.formikRef.current = formikBag);

            const canReset = formikBag.dirty && !formikBag.isSubmitting && !formikBag.isValidating;
            const canSubmit = formikBag.isValid && (canReset || validateOnMount);

            React.useLayoutEffect(() => {
                mounted.current = true;
                return () => {
                    mounted.current = false;
                };
            }, []);

            React.useLayoutEffect(() => {
                setSubmitError(props.error);
            }, [props.error]);

            React.useEffect(() => {
                submitError && scrollToRef(errorRef);
            }, [submitError]);

            return (
                <FormikProvider value={formikBag}>
                    <Form id={id.current} ref={ref} noValidate autoComplete={props.autoComplete}>
                        {props.preventSubmitOnEnter && (
                            <div style={{ display: 'none' }}>
                                <input
                                    data-testid="submit-prevent"
                                    type="submit"
                                    onClick={e => {
                                        e.preventDefault();
                                        return false;
                                    }}
                                />
                            </div>
                        )}
                        <Header
                            title={props.title}
                            subtitle={props.subtitle}
                            variant="h2"
                            translationOptions={props.translationOptions}
                        />
                        {props.children}
                        {submitError && (
                            <Error ref={errorRef} variant="body2" mb={2} data-testid="submit-error">
                                <Locale path={submitError} />
                            </Error>
                        )}
                        <Box display="flex" sx={{ justifyContent: props.alignSubmit }}>
                            <SubmitButtonComponent
                                data-testid="submit-button"
                                formId={id.current}
                                loading={formikBag.isSubmitting}
                                onClick={formikBag.handleSubmit}
                                disabled={!canSubmit}
                                sx={{ flexGrow: props.alignSubmit !== 'stretch' ? '0' : '1' }}
                            >
                                <Locale path={props.submitText} />
                            </SubmitButtonComponent>
                            {props.onReset ? (
                                <ResetButtonComponent
                                    data-testid="reset-button"
                                    onClick={formikBag.handleReset}
                                    disabled={!canReset}
                                    sx={{ flexGrow: props.alignSubmit !== 'stretch' ? '0' : '1' }}
                                >
                                    <Locale path={props.resetText} />
                                </ResetButtonComponent>
                            ) : null}
                        </Box>
                    </Form>
                </FormikProvider>
            );
        })
    );

FormikValidationForm.propTypes = {
    alignSubmit: PropTypes.oneOf([
        'flex-start',
        'flex-end',
        'center',
        'space-between',
        'space-around',
        'stretch',
    ]),
    autoComplete: PropTypes.string,
    children: PropTypes.node.isRequired,
    enableReinitialize: PropTypes.bool,
    error: PropTypes.string,
    formId: PropTypes.string,
    formikRef: PropTypes.object,
    initialValues: PropTypes.object,
    initialTouched: PropTypes.object,
    onReset: PropTypes.func,
    onSubmit: PropTypes.func,
    preventSubmitOnEnter: PropTypes.bool,
    resetText: PropTypes.string,
    resetButton: PropTypes.elementType,
    submitText: PropTypes.string,
    submitButton: PropTypes.elementType,
    subtitle: PropTypes.string,
    title: PropTypes.string,
    translationOptions: PropTypes.object,
    validateOnBlur: PropTypes.bool,
    validateOnChange: PropTypes.bool,
    validateOnMount: PropTypes.bool,
    validationSchema: PropTypes.oneOfType([
        PropTypes.object, // yup schema
        PropTypes.func, // function that returns a yup schema
    ]),
    validate: PropTypes.func,
};

FormikValidationForm.defaultProps = {
    alignSubmit: 'stretch',
    resetText: 'Reset',
    submitText: 'Submit',
    validateOnMount: true,
};

export default FormikValidationForm;
